import { forwardRef } from "react";
import { format } from "date-fns/format";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import type { LabReading } from "../types";
import { LabStatusBadge } from "../atoms/LabStatusBadge";
import { getDotColor } from "../utils";

export interface LabReadingListItemProps extends React.ComponentProps<"button"> {
  reading: LabReading;
  isSelected?: boolean;
  isLatest?: boolean;
}

export const LabReadingListItem = forwardRef<HTMLButtonElement, LabReadingListItemProps>(
  ({ reading, isSelected, isLatest, className, ...props }, ref) => {
    const { t } = useTranslation("ui");
    return (
      <button
        ref={ref}
        data-slot="lab-reading-list-item"
        className={cn(
          "w-full text-left px-5 py-3 flex items-center justify-between gap-3 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
          isSelected ? "bg-gray-50 dark:bg-gray-800/50" : "hover:bg-gray-50/60 dark:hover:bg-gray-800/30",
          className
        )}
        {...props}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "rounded-full flex-shrink-0 transition-all",
              isSelected ? "w-2.5 h-2.5" : "w-1.5 h-1.5",
              getDotColor(reading.statusIndicator)
            )}
          />
          <div>
            <p
              className={cn(
                "text-sm font-medium tabular-nums",
                isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
              )}
            >
              {reading.value}
              <span className="text-xs text-gray-400 dark:text-gray-500 font-normal ml-1">{reading.unit}</span>
              {isLatest && (
                <span className="ml-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                  {t("Latest")}
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{format(new Date(reading.date), "MMMM d, yyyy")}</p>
          </div>
        </div>
        <LabStatusBadge indicator={reading.statusIndicator} />
      </button>
    );
  }
);
LabReadingListItem.displayName = "LabReadingListItem";
